/**
 * Contadores de documentos del dashboard.
 *
 * v2 — los contadores ya NO se calculan en el cliente.
 *
 * ANTES: `getDocs` sobre la colección `document` (y `collectionGroup("legajo")`)
 *       sin `limit()`. Un RUC+año son ~23.996 documentos (186.499 en total) y una
 *       sola carga del dashboard costaba ~29.000 lecturas. El 97% del consumo
 *       del proyecto eran QUERY (escaneos sin límite) y el responsable era este
 *       panel de administración, no la app móvil.
 *
 * AHORA: se pide el resultado ya calculado a la Cloud Function
 *       `obtenerContadoresDocumentos` (onCall, us-central1), que lee desde el
 *       servidor y cachea. El cliente recibe 7 números, no 24.000 documentos.
 *
 * CONTRATO
 *   entrada : contadorDocumentos(ruc, ano, tipoDocumento = "Todos")
 *   salida  : {
 *               mensual:      { enero..diciembre }   // documentos enviados
 *               vistos:       { enero..diciembre }   // documentos vistos
 *               firmados:     { enero..diciembre }   // documentos firmados
 *               semanal:      { "09-SEM44": 12, ... } // clave "NN-SEMxx"
 *               total:        Number,               // total de documentos
 *               totalVistos:  Number,
 *               totalFirmados:Number
 *             }
 *   error   : si el callable falla (p. ej. todavía no está desplegado) se registra
 *             una advertencia clara y se devuelven los contadores vacíos con la
 *             misma forma, para que la pantalla siga renderizando. No hay
 *             reintentos ni bucles: como máximo un intento por carga.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp from "../firebase/credenciales";
import { registrarLecturas } from "./consumoLecturas";

const functions = getFunctions(firebaseApp, "us-central1");

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "setiembre", "octubre", "noviembre", "diciembre"
];

// Origen de atribución: la pantalla que pidió los contadores.
const ORIGEN = "dashboard:documentos";

// Cache en memoria con TTL de 5 minutos para evitar consultas redundantes repetidas
const cache = new Map();
const inFlightRequests = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;

/** Objeto con los 12 meses en español, todos en cero. */
const mesesEnCero = () =>
  MESES.reduce((acc, mes) => {
    acc[mes] = 0;
    return acc;
  }, {});

/** Forma de retorno vacía: la pantalla siempre recibe la misma estructura. */
const contadoresVacios = () => ({
  mensual: mesesEnCero(),
  vistos: mesesEnCero(),
  firmados: mesesEnCero(),
  semanal: {},
  total: 0,
  totalVistos: 0,
  totalFirmados: 0
});

/** Normaliza un objeto de meses (el que venga del servidor) a los 12 meses. */
const normalizarMeses = (valor) => {
  const salida = mesesEnCero();
  if (valor && typeof valor === "object") {
    for (const mes of MESES) {
      const n = Number(valor[mes]);
      if (Number.isFinite(n)) salida[mes] = n;
    }
  }
  return salida;
};

/** Normaliza el mapa semanal ("09-SEM44" -> 12). */
const normalizarSemanal = (valor) => {
  const salida = {};
  if (valor && typeof valor === "object") {
    for (const [clave, n] of Object.entries(valor)) {
      const cantidad = Number(n);
      if (Number.isFinite(cantidad)) salida[clave] = cantidad;
    }
  }
  return salida;
};

const sumar = (objetoMeses) =>
  Object.values(objetoMeses || {}).reduce((acc, n) => acc + (Number(n) || 0), 0);

/**
 * Extrae los contadores de la respuesta del callable.
 * Acepta tanto la forma plana ({ mensual, vistos, ... }) como una envoltura
 * ({ contadores: { ... } }); devuelve null si la respuesta no es utilizable.
 */
const extraerContadores = (response) => {
  const bruto =
    response && typeof response === "object" && "data" in response
      ? response.data
      : response;
  if (!bruto || typeof bruto !== "object") return null;

  const fuente =
    bruto.contadores && typeof bruto.contadores === "object" ? bruto.contadores : bruto;

  const tieneForma = [
    "mensual",
    "vistos",
    "firmados",
    "semanal",
    "total",
    "totalVistos",
    "totalFirmados",
  ].some((clave) => clave in fuente);

  return tieneForma ? fuente : null;
};

export default async function contadorDocumentos(ruc, ano, tipoDocumento = "Todos") {
  if (!ruc || !ano) {
    throw new Error("RUC y año son requeridos");
  }

  const cacheKey = `${String(ruc).trim()}_${String(ano).trim()}_${String(tipoDocumento).trim()}`;
  const cachedEntry = cache.get(cacheKey);
  const now = Date.now();

  if (cachedEntry && (now - cachedEntry.timestamp < CACHE_TTL_MS)) {
    return cachedEntry.data;
  }

  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const fetchPromise = (async () => {
    try {
      const callable = httpsCallable(functions, "obtenerContadoresDocumentos");
      const response = await callable({
        ruc,
        ano: String(ano),
        tipoDocumento,
      });

      const datos = extraerContadores(response);
      if (!datos) {
        console.warn(
          "contadorDocumentos: la Cloud Function 'obtenerContadoresDocumentos' respondió con una forma inesperada. Se devuelven contadores vacíos.",
          response
        );
        return contadoresVacios();
      }

      const mensual = normalizarMeses(datos.mensual);
      const vistos = normalizarMeses(datos.vistos);
      const firmados = normalizarMeses(datos.firmados);
      const semanal = normalizarSemanal(datos.semanal);

      const total = Number.isFinite(Number(datos.total))
        ? Number(datos.total)
        : sumar(mensual);
      const totalVistos = Number.isFinite(Number(datos.totalVistos))
        ? Number(datos.totalVistos)
        : sumar(vistos);
      const totalFirmados = Number.isFinite(Number(datos.totalFirmados))
        ? Number(datos.totalFirmados)
        : sumar(firmados);

      const resultado = {
        mensual,
        vistos,
        firmados,
        semanal,
        total,
        totalVistos,
        totalFirmados,
      };

      // El servidor es quien lee los documentos; aquí solo se informa de cuántos
      // agregó para poder atribuir el consumo a la pantalla que lo pidió.
      registrarLecturas({ contadoresDocumentos: total }, { origen: ORIGEN });

      cache.set(cacheKey, { timestamp: Date.now(), data: resultado });
      return resultado;
    } catch (error) {
      // El dashboard no debe romperse si la Cloud Function aún no está
      // desplegada (o falla). Se avisa por consola y se devuelven ceros.
      console.warn(
        `contadorDocumentos: no se pudieron obtener los contadores para RUC ${ruc} y año ${ano}. ` +
          `Se devuelven contadores vacíos. Detalle:`,
        error?.message || error
      );
      return contadoresVacios();
    }
  })();

  inFlightRequests.set(cacheKey, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    inFlightRequests.delete(cacheKey);
  }
}
